/**
 * Fetch every page of a paginated endpoint, instead of asking for one enormous one.
 *
 * Asking for `size: 100000` does not get you 100,000 rows. The server caps a page at
 * `spring.data.web.pageable.max-page-size` and returns the first page with HTTP 200, so a
 * caller that treats the response as complete quietly loses everything past the cap. That
 * is how the worksheet export came to produce a spreadsheet of 600 rows out of 87,286, and
 * how the lab-result export produced 600 out of 19,397 - both looking perfectly normal.
 *
 * `totalPages` in the response reflects the size the SERVER used, not the size that was
 * asked for, so looping on it cannot be fooled by a clamp.
 *
 * @param {(params: object) => Promise<object>} fetchPage  called with {page, size, ...};
 *        must resolve to a Spring Page ({content, totalPages}).
 * @param {object} baseParams  query params minus page/size.
 * @param {object} [options]
 * @param {number} [options.size=200]       rows per request. 200 is servable on every
 *        profile (the default ceiling is 200, production currently allows 600).
 * @param {number} [options.maxPages=500]   stop-loss, so a bad totalPages cannot spin
 *        forever. 200 x 500 = 100,000 rows.
 * @returns {Promise<Array>} every row, in page order.
 */
export async function fetchAllPages(fetchPage, baseParams = {}, options = {}) {
  const size = options.size || 200;
  const maxPages = options.maxPages || 500;

  const rows = [];
  let page = 0;
  let totalPages = 1;

  do {
    const response = await fetchPage({ ...baseParams, page, size });
    if (!response) {
      break;
    }
    // Some callers hand back the axios response, others the body.
    const body = response.data && response.data.content ? response.data : response;
    const content = body.content || [];
    rows.push(...content);

    totalPages = body.totalPages || 1;
    page += 1;

    // A page that comes back empty means there is nothing further to read, whatever
    // totalPages claims.
    if (content.length === 0) {
      break;
    }
  } while (page < totalPages && page < maxPages);

  return rows;
}

/**
 * Add `id` as a final tiebreaker to a sort.
 *
 * Paging through a changing table with a non-unique sort key can show the same row twice
 * or skip one entirely: rows sharing a timestamp have no defined order, so the database
 * may place them differently on each query. Exports are the common victim, because they
 * read every page.
 *
 * @param {string} sort  e.g. "testTime,desc"
 * @returns {string[]}   e.g. ["testTime,desc", "id,desc"]
 */
export function stableSort(sort) {
  if (!sort) {
    return ["id,desc"];
  }
  if (/(^|,)id(,|$)/.test(sort)) {
    return [sort];
  }
  const direction = sort.endsWith(",asc") ? "asc" : "desc";
  return [sort, `id,${direction}`];
}
